/**
 * Downloads Page — Patch 34.1.20 · Founder-only sanitized-archive downloader.
 *
 * The `/api/v1/downloads` endpoint requires the founder JWT (see Patch
 * 34.1.18). This page uses the authenticated session token to list
 * archives and stream them to the browser via Blob/ObjectURL, so the
 * founder never has to leave the app or drop to a terminal.
 *
 * If the caller is not a founder the API returns 401/403 — the page
 * shows a friendly "founder access required" state.
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { http, ApiError } from "@/shared/api/http";
import { config } from "@/shared/config";
import { useAuthStore } from "@/entities/auth/store";
import { Download, ShieldAlert, Loader2, FileArchive } from "lucide-react";

interface DownloadEntry {
  filename: string;
  size_bytes: number;
  size_mb: number;
  url: string;
  modified_at: number;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "forbidden" }
  | { kind: "error"; message: string }
  | { kind: "ready"; entries: DownloadEntry[] };

function humanBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function humanDate(unix: number): string {
  return new Date(unix * 1000).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function DownloadsPage() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [busy, setBusy] = useState<string | null>(null);
  const token = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const entries = await http<DownloadEntry[]>("/downloads", {
          method: "GET",
        });
        if (cancelled) return;
        setState({ kind: "ready", entries });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          setState({ kind: "forbidden" });
        } else {
          setState({
            kind: "error",
            message: err instanceof Error ? err.message : "Failed to load downloads.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function downloadOne(entry: DownloadEntry) {
    setBusy(entry.filename);
    try {
      // entry.url already includes the /api/v1 prefix (e.g.
      // "/api/v1/downloads/foo.zip") — do NOT concatenate config.apiPrefix
      // again or the request 404s.
      const url = `${config.backendUrl}${entry.url}`;
      const resp = await fetch(url, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        throw new Error(`Download failed (${resp.status})`);
      }
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = entry.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Release memory after the browser has queued the save.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Download failed.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8 p-6" data-testid="downloads-page">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight" data-testid="downloads-title">
          Sanitized Source Archives
        </h1>
        <p className="text-sm text-muted-foreground">
          Founder-only. Each archive is a sanitized full-source snapshot with a
          companion sanitization report. Every download is authenticated with
          your session — no public link is issued.
        </p>
      </header>

      {state.kind === "loading" && (
        <Card data-testid="downloads-loading">
          <CardContent className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading archives…
          </CardContent>
        </Card>
      )}

      {state.kind === "forbidden" && (
        <Card className="border-amber-300" data-testid="downloads-forbidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <ShieldAlert className="h-5 w-5" />
              Founder access required
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            The downloads endpoint is gated to founder accounts per Patch
            34.1.18. Sign in as the founder to view this page, or ask the
            founder to generate a tokenized share link.
          </CardContent>
        </Card>
      )}

      {state.kind === "error" && (
        <Card className="border-red-300" data-testid="downloads-error">
          <CardHeader>
            <CardTitle className="text-red-700">Couldn&apos;t load archives</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">{state.message}</CardContent>
        </Card>
      )}

      {state.kind === "ready" && state.entries.length === 0 && (
        <Card data-testid="downloads-empty">
          <CardContent className="p-6 text-sm text-muted-foreground">
            No archives are available yet.
          </CardContent>
        </Card>
      )}

      {state.kind === "ready" && state.entries.length > 0 && (
        <div className="space-y-3" data-testid="downloads-list">
          {state.entries.map((entry) => {
            const isBusy = busy === entry.filename;
            return (
              <Card
                key={entry.filename}
                data-testid={`downloads-row-${entry.filename}`}
                className="transition-shadow hover:shadow-md"
              >
                <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <FileArchive className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span
                        className="truncate font-mono text-sm"
                        title={entry.filename}
                        data-testid={`downloads-filename-${entry.filename}`}
                      >
                        {entry.filename}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span data-testid={`downloads-size-${entry.filename}`}>
                        {humanBytes(entry.size_bytes)}
                      </span>
                      <span>Modified {humanDate(entry.modified_at)}</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => downloadOne(entry)}
                    disabled={isBusy}
                    data-testid={`downloads-button-${entry.filename}`}
                    className="sm:w-40"
                  >
                    {isBusy ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Downloading…
                      </>
                    ) : (
                      <>
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
